import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuTrigger,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel
} from "@/components/ui/dropdown-menu";
import { User, LogOut, LogIn, ChevronUp } from "lucide-react";
import { useParams } from "react-router-dom";
import { useClientAuth } from "@/hooks/useClientAuth";
import ClientLoginDialog from "@/components/ClientLoginDialog";
import { cn } from "@/lib/utils";

const AccountFloatingButton = () => {
  const { clientId } = useParams();
  const { isAuthenticated, email, logout } = useClientAuth();
  const [isOpen, setIsOpen] = useState(false);

  // Ne rien afficher si on n'est pas sur une page client
  if (!clientId) {
    return null;
  }

  return (
    <div className="fixed bottom-6 left-6 z-50">
      {isAuthenticated && email ? (
        // Utilisateur connecté - Dropdown avec infos
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="default"
              size="sm"
              className={cn(
                "h-12 px-4 rounded-full shadow-lg transition-all duration-300 ease-out",
                "bg-primary hover:bg-primary/90 text-primary-foreground",
                "hover:shadow-xl hover:scale-105",
                "border-2 border-primary-foreground/20"
              )}
            >
              <User className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline font-medium">Mon compte</span>
              <ChevronUp className={cn(
                "w-3 h-3 ml-2 transition-transform duration-200",
                isOpen && "rotate-180"
              )} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent 
            side="top" 
            align="start"
            className="w-64 mb-2 shadow-xl border-border/50"
          >
            <DropdownMenuLabel className="px-3 py-2">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="w-4 h-4 text-primary" />
                </div>
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-foreground">Connecté en tant que</span>
                  <span className="text-xs text-muted-foreground truncate">{email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={logout}
              className="px-3 py-2 cursor-pointer hover:bg-destructive/10 focus:bg-destructive/10"
            >
              <LogOut className="w-4 h-4 mr-2 text-destructive" />
              <span className="text-destructive">Se déconnecter</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        // Utilisateur non connecté - Bouton de connexion
        <ClientLoginDialog
          triggerButton={
            <Button
              variant="default"
              size="sm"
              className={cn(
                "h-12 px-4 rounded-full shadow-lg transition-all duration-300 ease-out",
                "bg-primary hover:bg-primary/90 text-primary-foreground",
                "hover:shadow-xl hover:scale-105",
                "border-2 border-primary-foreground/20"
              )}
            >
              <LogIn className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline font-medium">Se connecter</span>
            </Button>
          }
        />
      )}
    </div>
  );
};

export default AccountFloatingButton;
